// Astrea Ops Platform - read-only access to a person's `documents` rows.
//
// `documents` is real, migrated infrastructure (see
// 20260915000005_risk_governance.sql, extended for personnel files rather than
// just project deliverables in 20260915000008_phase5_corrections.sql), but until
// now nothing in the frontend ever read it back - the approved-subcontractor-
// upload flow (api/subcontractor-review-upload.js) was the only thing writing to
// it. This is the first read.
//
// Plain RLS-gated Supabase query, same pattern as every other *.js file in
// src/lib - documents_select already permits Senior Management/Finance/POM (see
// can_see_files() in 20260919000001_senior_management_and_system_admin.sql,
// which returns true for those three roles regardless of project_id, so a
// person-scoped row with project_id null still passes). That's the same
// audience (Senior Management, or a System Admin) who can already open a
// person's Edit screen from the Team page, so no new exposure here.

import { supabase } from './supabaseClient';

function toPersonDocument(row) {
  return {
    id: row.id,
    name: row.name,
    category: row.category || '',
    status: row.doc_status,
    link: row.link || '',
    filePath: row.file_path || '',
    notes: row.notes || '',
    authorId: row.author_id,
    reviewedBy: row.reviewed_by,
    addedAt: row.added_at,
  };
}

export async function loadPersonDocuments(personId) {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('person_id', personId)
    .order('added_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(toPersonDocument);
}

// Calls the authenticated delete endpoint (not a direct Supabase delete) because
// the underlying Storage file - when this document came from the Upload Link flow -
// has no client-reachable delete path at all (the bucket has no storage.objects
// policies; every upload/approval already went through a signed URL minted
// server-side). See api/delete-uploaded-document.js.
export async function deletePersonDocument(documentId) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in.');
  const res = await fetch('/api/delete-uploaded-document', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ documentId }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Failed to delete this document.');
  return body;
}

// v7.63 (7 Oct 2026): uploaded files are no longer opened from a link stored for 10 years.
// The server checks the signed-in user may see this document (the same rule as the
// documents table: W-9s and "Other" are Senior Management / Finance only) and hands back a
// link that works for five minutes. Documents added by hand with an outside link (Dropbox,
// Drive) still open that link.
export async function openPersonDocument(doc) {
  if (!doc.filePath) {
    if (doc.link) return doc.link;
    throw new Error('This document has no file.');
  }
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in.');
  const res = await fetch('/api/document-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ documentId: doc.id }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.url) throw new Error(body.error || 'Could not open this document.');
  return body.url;
}
